import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core'
import { provideRouter } from '@angular/router'
import { provideStoreDevtools } from '@ngrx/store-devtools'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'

import { routes } from './app.routes'
import { provideStore } from '@ngrx/store'
import { rootReducer } from '@/store'
import { localStorageSyncReducer } from '@/store/layout/layout-reducers'
import { provideEffects } from '@ngrx/effects'
import { AuthenticationEffects } from '@/store/authentication/authentication.effects'
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http'
import { DecimalPipe } from '@angular/common'
import { CookieService } from 'ngx-cookie-service'
import { BrowserModule } from '@angular/platform-browser'
import { JwtInterceptor } from '@core/helper/jwt.interceptor'
import { ErrorInterceptor } from '@core/helper/error.interceptor'
import { BackendService } from '@core/services/backend.service'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc'
import { OpenAuthenticationService } from '@core/services/oauth.service'
import { HybridOAuthStorage } from '@core/services/hybrid-oauth-storage'

export function initializeAuth() {
  const authService = inject(OpenAuthenticationService);
  // Return the promise so app init BLOCKS until discovery + token exchange complete and the token is in the
  // store — otherwise routing/guards run too early and authGuard loops back into login.
  return authService.initAuth();
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    DecimalPipe,
    CookieService,
    BackendService,
    provideOAuthClient(),
    // Persist OIDC session TOKENS in localStorage (shared across same-origin tabs) so a new tab inherits the
    // session instead of forcing a re-login — but keep the transient nonce/PKCE_verifier in per-tab
    // sessionStorage so concurrent login flows (e.g. several tabs re-logging-in after a cross-tab logout) can't
    // clobber each other's nonce/state. Tradeoff accepted for this internal staff portal — mitigated by CSP,
    // no HTML-injection sinks, short access tokens, and the 401→re-login handler.
    { provide: OAuthStorage, useClass: HybridOAuthStorage },
    provideAppInitializer(initializeAuth),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideStore(rootReducer, { metaReducers: [localStorageSyncReducer] }),
    importProvidersFrom(BrowserAnimationsModule, BrowserModule),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideEffects(AuthenticationEffects),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    importProvidersFrom(FormsModule, ReactiveFormsModule)
  ],
}
